import { PERMISSIONS } from '@/components'
import { Customer } from '@/modules/customer/customer.schema'
import { Devlop } from '@/modules/devlop/devlop.schema'
import { Production } from '@/modules/production-project/production.schema'
import { GetTransformedEnumObjEnumType, getTransformedEnumObj } from '@/utils/enum-obj-transform'

export type Risk = '无' | '低' | '中' | '高' | '极高' | null

// t('STAGE') t('PROJECT') t('PORTFOLIO') t('OBJECTIVE') t('TEAM') t('CUSTOMER') t('PRODUCTION') t('DEVLOP')
export enum ResourceType {
  STAGE = -1,
  PROJECT = 0,
  PORTFOLIO = 1,
  OBJECTIVE = 2,
  TEAM = 3,
  CUSTOMER = 5,
  PRODUCTION = 6,
  DEVLOP = 7,
}

// [selfId, resourcePoint, permission]
export type Permissions = [string, string, number][] | null

export interface ResourcePermission {
  permissions: Permissions
}

export enum FetchType {
  fetchChild = 1,
  fetchParent = 2,
}

export enum PpUserIssueSaveType {
  JQL = 0,
  SEARCH = 1,
}

export enum JiraTableColumnType {
  Jira = 0,
  Other = 1,
}

export enum ProjectType {
  DEFAULT = 0,
  RELEASE = 1, // 发版项目
}

export enum ReleaseType {
  MAIN = 'MAIN', // 主版本
  MAIN_WEEKLY = 'MAIN_WEEKLY', // 主版weekly版本
  CUSTOM_SPECIAL = 'CUSTOM_SPECIAL', // 客户特殊版
  BIG_CUSTOM_LONG_TERM = 'BIG_CUSTOM_LONG_TERM', // 大客户长期支持版
  TECH = 'TECH', // 技术打磨版
}

export enum ProjectStatus {
  未开始 = 0,
  进行中 = 1,
  已结项 = 2,
  暂停 = 3,
  计划中 = 4,
  // '结项中' = 5,
}

export type Resource = Project | Portfolio | Team | Customer | Production | Devlop

export const ProjectClassification = getTransformedEnumObj({
  // 2025.01.06 新增
  其他: 8,
  音视频: 9,
  SDK与业务工程: 10,
  云平台: 11,
  基础架构与应用: 12,
  数据平台: 13,
  QA: 14,
  LAB: 15,
} as const)

export type ProjectClassificationType = GetTransformedEnumObjEnumType<typeof ProjectClassification, string>

export enum PriorityEnum {
  P0 = 0,
  P1 = 1,
  P2 = 2,
}

export interface BaseResource {
  priority: PriorityEnum
}

export interface Project extends BaseResource, ResourcePermission {
  selfId: string
  type: ResourceType.PROJECT
  name: string
  projectType: ProjectType
  hots: any[]
  isFavourited: boolean
  email?: string
  jql?: string | null
  risk?: Risk
  progress?: number
  classification?: ProjectClassificationType
  source?: string
  industryId?: number
  industry?: Industry
  scenarioId?: number
  scenario?: Scenario
  goal?: string
  background?: string
  goalDescription?: string
  checkStandard?: string
  startDate?: string
  endDate?: string
  challenge: string
  departmentId?: number
  status?: number
  lastestWrite?: ReportBase | null
  thisWeekReport?: ReportBase | null
  evaluation?: Evaluation
  projectStages?: ProjectStage[]
  relations?: PpRelation[]
  releaseNumber?: string | null
  releaseType?: ReleaseType | null
  releaseJiraKey?: string | null
  workResources?: ProjectWorkResourceEntity[]
  workResourceRelations?: ProjectWorkResourceRelation[]
}

export interface ProjectUserStageDate {
  id: number
  userStageId: number
  startDate: string
  endDate: string
  count: number
}

export enum ProjectUserStageType {
  OWNER = 1,
  PARTICIPATOR = 2,
}

export interface ProjectUserStage {
  id: number
  stageId: number
  email: string
  type: ProjectUserStageType
  count: number
  workload: number
  dates: ProjectUserStageDate[]
}

export interface ProjectStage {
  ename: string
  id: number
  selfId: string
  email: string
  name: string
  description: string
  planStartDate: string | null
  planEndDate: string | null
  actualStartDate: string | null
  actualEndDate: string | null
  progress: number
  weight: number
  status: number
  stageId?: number | null
  stageGroupId?: number
  permissions: PERMISSIONS
  userStages: ProjectUserStage[]
}

export interface Portfolio extends BaseResource, ResourcePermission {
  hots: any[]
  selfId: string
  email: string
  type: ResourceType.PORTFOLIO
  name: string
  description: string
  background: string
  goal: string
  standard: string
  challenge: string
  classification: ProjectClassificationType
  risk: Risk
  startDate: string
  endDate: string
  departmentId: number
  lastestWrite: ReportBase | null
  thisWeekReport: ReportBase | null
  isFavourited: boolean
  progress: number
  readPermissions?: string[]
}

export interface Team extends ResourcePermission {
  selfId: string
  type: ResourceType.TEAM
  email: string
  name: string
  description: string
  risk: Risk
  startDate: string
  endDate: string
  departmentId: number
  lastestWrite: ReportBase | null
  thisWeekReport: ReportBase | null
  isFavourited: boolean
  progress: number
  readPermissions?: string[]
}

export interface ResourceUser {
  selfId: string
  email: string
  permissions: Permissions
  roles: string[]
}

export interface ResourceEvaluation {
  id: number
  selfId: string
  type: number // 1: summary, 0: 其他
  status: number // 0: 未填写, 1: 已填写
  email: string
  highlightMembers: string[]
  deadline: string
  delayRatio: number
  createdAt: string
  completion: number
  satisfaction: string
  expectation: string
  content: string
}

export interface MyProjectStage extends ProjectStage {
  project: Project
}

// 组合
export interface PpRelationBase {
  id: number
  selfId: string
  parentSelfId: string
  parentType: ResourceType
  children: PpRelation[]
}

export interface PpRelationProject extends PpRelationBase {
  type: ResourceType.PROJECT
  project: Project & {
    projectStages: ProjectStage[]
  }
  portfolio: never
  objective: never
  customer: never
  production: never
  devlop: never
}

export interface PpRelationPortfolio extends PpRelationBase {
  type: ResourceType.PORTFOLIO
  project: never
  objective: never
  portfolio: Portfolio
  customer: never
  production: never
  devlop: never
}

export interface PpRelationObjective extends PpRelationBase {
  type: ResourceType.OBJECTIVE
  project: never
  portfolio: never
  objective: PpObjective
  customer: never
  production: never
  devlop: never
}

export interface PpRelationCustomer extends PpRelationBase {
  type: ResourceType.CUSTOMER
  project: never
  portfolio: never
  objective: never
  customer: Customer
  production: never
  devlop: never
}

export interface PpRelationProduction extends PpRelationBase {
  type: ResourceType.PRODUCTION
  project: never
  portfolio: never
  objective: never
  customer: never
  production: Production
  devlop: never
}

export interface PpRelationDevlop extends PpRelationBase {
  type: ResourceType.DEVLOP
  project: never
  portfolio: never
  objective: never
  customer: never
  production: never
  devlop: Devlop
}

export type PpRelation =
  | PpRelationProject
  | PpRelationPortfolio
  | PpRelationCustomer
  | PpRelationProduction
  | PpRelationDevlop

// 周报
export interface ReportExtra {
  id: number
  reportId: number
  field: string
  fieldId?: string
  content: string
  order: number
  updatedAt: string
}

export interface ReportSnapshot extends ResourcePermission {
  departmentId: number
  id: string
  selfId: string
  reportId: number
  parentId: string
  path: string
  report: Report
  name: string
  description: string
  status: string
  progress: number
  risk: string
  email: string
  type: ResourceType
  startDate: string
  endDate: string
  weight: number
  moduleId: string
  order: number
  config: ReportConfiguration
}

export interface ReportBase extends ResourcePermission {
  id: number
  selfId: string
  type: ResourceType
  risk: Risk
  startDate: string
  endDate: string
  extras: ReportExtra[]
  snapshots: ReportSnapshot[]
  status: 0 | 1 | 2 // 2: 已完成
  createdAt: string
  updatedAt: string
  progress?: number
}

export type Report = ReportBase

// OKR
export interface PpOKR {
  id: number
  type: ResourceType
  parentObjective: PpObjective
  project?: Project
  children: PpOKR[]
}

export interface PpObjective {
  cycleId: number
  departmentId: number
  email: string
  name: string
  objectiveId: number
  parentId: number
  progress: number
}

export interface PpObjectiveData {
  page: number
  pageSize: number
  total: number
  objectives: PpObjective[]
}

export interface Evaluation {
  completion: number
  satisfaction: string
  delayRatio: number
  delayRatioDesc: string
  content: string
  email: string
  status: number
  type: number
  highlightMembers: string[]
}

export interface Industry {
  id: number
  name: string
  ename: string
}

export interface Scenario {
  id: number
  industryId: number
  name: string
  ename: string
}

export interface File {
  fileId: number
  category: string
  title: string
  email: string
  name?: string
  type?: string
  size?: number
  url: string
}

export interface PpFile {
  ppFileId: number
  selfId: string
  type: 0 | 1
  fileId: number
  file: File
  order?: number
}

export interface ReportConfiguration {
  id?: number
  selfId: string
  type: ResourceType
  effectiveDate: string
  modules: ReportConfigurationModule[]
}

export interface ReportConfigurationModule {
  id: string
  name: string
  order: number | null
  isDefault: boolean
  isChecked: boolean
  isExtra: boolean
  isDeleted: boolean
  isImmediately: boolean
  objectiveCycleId?: number
  fields: ReportConfigurationModuleField[]
}

export interface ReportConfigurationModuleField {
  id: string
  name: string
  order: number | null
  isDefault: boolean
  isChecked: boolean
  isDeleted: boolean
}

export enum WorkResourceRowType {
  Resource = 'resource', // department level only
  Portfolio = 'portfolio',
  Project = 'project',
  Custom = 'custom',
  Department = 'department',
  User = 'user',
}

export interface UserQuarterWorkResource {
  selfId?: string
  userWorkResourceSelfId?: string
  year: string
  quarter: string
  workload: number | null
  archivedLevel?: number
  archivedProjectBy?: string | null
  archivedQuarterBy?: string | null
}

export interface UserWeekWorkResourceComment {
  id: number
  userWeekWorkResourceId: number
  comment: string
}

export interface UserWeekWorkResource {
  id?: number
  selfId?: string
  userWorkResourceSelfId?: string
  year: string
  week: string
  startDate: string
  endDate: string
  workload: number | null
  comment?: UserWeekWorkResourceComment | null
  isArchived?: boolean
}

export interface SpecificProjectWorkResource {
  key: string
  type: WorkResourceRowType
  subprojectWorkResourceSelfId: string | null
  userWorkResourceSelfId: string | null
  subprojectName: string | null
  subprojectSelfId: string | null
  departmentId: number
  departmentName: string
  email: string
  predictWorkload: number | null
  actualWorkload: number | null
  startDate: string | null
  endDate: string | null
  project?: Project
  portfolio?: Portfolio
  departments?: { departmentId: number; departmentName: string }[]
  users?: string[]
  weeks: UserWeekWorkResource[]
  children?: SpecificProjectWorkResource[]
  isArchived?: boolean
  invisibleDate?: string | null
}

export interface DepartmentProjectWorkResource {
  key: string
  type: WorkResourceRowType
  projectWorkResourceSelfId: string | null
  subprojectWorkResourceSelfId: string | null
  userWorkResourceSelfId: string | null
  subprojectName: string | null
  subprojectSelfId: string | null
  email: string
  predictWorkload: number | null
  actualWorkload: number | null
  startDate: string | null
  endDate: string | null
  weeks: UserWeekWorkResource[]
  subproject?: Project
  isArchived?: boolean
  project?: (Customer | Devlop | Production) & { resourceType: ResourceType }
  children?: DepartmentProjectWorkResource[]
  invisibleDate?: string | null
}

export type DepartmentPeopleWorkResource = DepartmentProjectWorkResource

export interface ProjectWorkResourceEntity {
  id: number
  selfId: string
  projectSelfId: string
  customer?: Customer
  production?: Production
  devlop?: Devlop
}
export interface ProjectWorkResourceRelation {
  projectWorkResourceSelfId: string
  subprojectWorkResourceSelfId: string
  proportion: number
  project?: ProjectWorkResourceEntity
}

export enum SubprojectWorkResourceType {
  Project = 1,
  Custom = 2,
}

export enum ProjectWeekWorkResourceSnapshotType {
  CUSTOMER = ResourceType.CUSTOMER,
  DEVLOP = ResourceType.DEVLOP,
  PRODUCTION = ResourceType.PRODUCTION,
  PROJECT = ResourceType.PROJECT,
  // 大项目自定义类型
  SPECIFIC_PROJECT_CUSTOM = 100,
}

export enum PpRelationAuditStatus {
  审批中 = 1,
  审批通过 = 2,
  审批拒绝 = 3,
  审批撤回 = 4,
  移除关系 = 5,
}

export interface PpUserRole {
  id: number
  selfId: string
  type: ResourceType
  email: string
  role: string
  roleId: number | number
  createdAt: Date
  updatedAt: Date
}

export enum PAGE_RESOURCE_POINTS_ENUM {
  全部 = 'ALL',
  概况 = 'ALL_OVERVIEW',
  概况_名称 = 'ALL_OVERVIEW_NAME',
  概况_负责人 = 'ALL_OVERVIEW_OWNER',
  概况_周期 = 'ALL_OVERVIEW_CYCLE',
  概况_所属部门 = 'ALL_OVERVIEW_DEPARTMENT',
  '概况_行业/场景' = 'ALL_OVERVIEW_SaI',
  概况_分类 = 'ALL_OVERVIEW_CLASSIFICATION',
  概况_来源 = 'ALL_OVERVIEW_SOURCE',
  概况_优先级 = 'ALL_OVERVIEW_PRIORITY',
  概况_状态 = 'ALL_OVERVIEW_STATUS',
  概况_进度 = 'ALL_OVERVIEW_PROGRESS',
  概况_发版项目 = 'ALL_OVERVIEW_RELEASE',
  '概况_交付物/验收标准' = 'ALL_OVERVIEW_STANDARD',
  概况_挑战性 = 'ALL_OVERVIEW_CHALLENGE',

  概况_成员 = 'ALL_OVERVIEW_MEMBER',
  概况_关联子集 = 'ALL_OVERVIEW_CHILDREN',
  概况_关联OKR = 'ALL_OVERVIEW_OBJECTIONS',
  概况_关联父项目 = 'ALL_OVERVIEW_PARENT-PROJECT',
  概况_关联父组合 = 'ALL_OVERVIEW_PARENT-PORTFOLIO',

  概况_历史记录 = 'ALL_OVERVIEW_HISTORY',
  概况_所属区域 = 'ALL_OVERVIEW_AREA',
  概况_服务等级 = 'ALL_OVERVIEW_LEVEL',
  概况_依赖项 = 'ALL_OVERVIEW_DEPENDENCE',
  概况_背景 = 'ALL_OVERVIEW_BACKGROUND',
  概况_业务目标 = 'ALL_OVERVIEW_BUSINESS-GOAL',
  概况_交付目标 = 'ALL_OVERVIEW_DELIVERY-GOAL',
  概况_预计交付时间 = 'ALL_OVERVIEW_EXPECT-TIME',
  概况_交付时间 = 'ALL_OVERVIEW_DELIVERY-TIME',
  概况_产品线 = 'ALL_OVERVIEW_PRODUCTION-LINE',
  概况_受益地区 = 'ALL_OVERVIEW_BENEFICIARY-AREA',
  概况_四分类 = 'ALL_OVERVIEW_FOUR-KIND',
  概况_产品分类 = 'ALL_OVERVIEW_PRODUCTION-CLASSIFICATION',
  概况_期望交付时间 = 'ALL_OVERVIEW_EXPECT-DELIVERY-TIME',
  概况_实际交付时间 = 'ALL_OVERVIEW_ACTUAL-DELIVERY-TIME',
  概况_确认交付时间 = 'ALL_OVERVIEW_CONFIRM-DELIVERY-TIME',
  概况_产品阶段 = 'ALL_OVERVIEW_PRODUCTION-STAGE',
  概况_产品上线时间 = 'ALL_OVERVIEW_PRODUCTION-RELEASE-TIME',
  概况_PMF完成时间 = 'ALL_OVERVIEW_PMF-COMPLETE-TIME',
  概况_PMF项目目标 = 'ALL_OVERVIEW_PMF-GOAL',
  概况_阶段目标 = 'ALL_OVERVIEW_STAGE-GOAL',
  概况_目标类型 = 'ALL_OVERVIEW_GOAL-TYPE',
  概况_目标完成时间 = 'ALL_OVERVIEW_GOAL-COMPLETE-TIME',
  概况_组合描述 = 'ALL_OVERVIEW_DESCRIPTION',
  概况_审批意见 = 'ALL_OVERVIEW_AUDIT-COMMENT',
  概况_资源预估 = 'ALL_OVERVIEW_PREDICT-WORKLOAD',

  资源看板 = 'ALL_DASHBOARD-USER-RESOURCE',
  资源管理 = 'ALL_USER-RESOURCE',
  审批 = 'ALL_AUDIT',
  目标 = 'ALL_OBJECTIVES',
  '项目/组合' = 'ALL_PROJECT-PORTFOLIO',
  阶段与任务 = 'ALL_STAGE',
  需求与问题 = 'ALL_JIRA',
  需求与问题_配置 = 'ALL_JIRA_CONFIG',
  需求与问题_内容 = 'ALL_JIRA_CONTENT',
  文件 = 'ALL_FILE',
  结项 = 'ALL_EVALUATION',
  周报 = 'ALL_REPORT',
  周报_配置 = 'ALL_REPORT_CONFIG',
  周报_内容 = 'ALL_REPORT_CONTENT',
}

export const resourceTypeTabMap = {
  [ResourceType.CUSTOMER]: ['WHITELIST', 'WHITELIST_US&ROW', 'WHITELIST_CN'], //  按项目区域区分
  [ResourceType.PRODUCTION]: ['WHITELIST', 'WHITELIST_US', 'WHITELIST_R&D', 'WHITELIST_OTHER'], // 按负责人所属团队区分
  default: ['WHITELIST'],
}

export interface PpRolePermission {
  id: number
  type: string
  role: string
  resourcePoint: string
  permission: number
}

export enum TeamRole {
  负责人 = '负责人',
  成员 = '成员',
  其他 = '其他',
}

export enum SpecialProjectFormLocalStorageKey {
  DEVLOP = 'devlop-form-cache',
  PRODUCTION = 'production-form-cache',
  CUSTOMER = 'customer-form-cache',
}
